import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import LandingHeader from '../Shared/LandingHeader';
import './RegistrationPage.css';

const RegistrationPage = () => {
    const { t, i18n } = useTranslation(['auth', 'common', 'navigation']);
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
        studentClass: '',
        stream: '',
        board_id: '',
        class_id: '',
        subject_id: '',
        chapter_id: '',
        profile_language: i18n.language || 'en'
    });


    const navigate = useNavigate();

    const [boards, setBoards] = useState([]);
    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [availableLanguages, setAvailableLanguages] = useState([]);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchBoards();
        fetchLanguages();
    }, []);

    // Sync form language with i18n language
    useEffect(() => {
        setFormData(prev => ({ ...prev, profile_language: i18n.language }));
    }, [i18n.language]);

    const fetchLanguages = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}get_languages.php`, { credentials: 'include' });
            const data = await response.json();
            if (data.status === 'success') {
                setAvailableLanguages(data.languages);
            }
        } catch (error) {
            console.error("Error fetching languages:", error);
        }
    };

    const fetchBoards = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}get_boards.php`, { credentials: 'include' });
            const data = await response.json();
            setBoards(data);
        } catch (error) {
            console.error("Error fetching boards:", error);
        }
    };

    const fetchClasses = async (boardId) => {
        try {
            const response = await fetch(`${API_BASE_URL}get_classes.php?board_id=${boardId}`, { credentials: 'include' });
            const data = await response.json();
            setClasses(data);
            setSubjects([]); // Reset subjects when board changes
        } catch (error) {
            console.error("Error fetching classes:", error);
        }
    };

    const fetchSubjects = async (classId) => {
        try {
            const response = await fetch(`${API_BASE_URL}get_subjects.php?class_id=${classId}`, { credentials: 'include' });
            const data = await response.json();
            setSubjects(data);
            setChapters([]);
        } catch (error) {
            console.error("Error fetching subjects:", error);
        }
    };

    const fetchChapters = async (subjectId) => {
        try {
            const response = await fetch(`${API_BASE_URL}get_chapters.php?subject_id=${subjectId}`, { credentials: 'include' });
            const data = await response.json();
            setChapters(data);
        } catch (error) {
            console.error("Error fetching chapters:", error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'board_id') {
            setFormData(prev => ({
                ...prev,
                board_id: value,
                class_id: '',
                subject_id: '',
                studentClass: '',
                stream: ''
            }));
            if (value) fetchClasses(value);
            else {
                setClasses([]);
                setSubjects([]);
            }
            return;
        }

        if (name === 'class_id') {
            const selectedClass = classes.find(c => String(c.id) === String(value));
            setFormData(prev => ({
                ...prev,
                class_id: value,
                subject_id: '',
                chapter_id: '',
                studentClass: selectedClass ? selectedClass.class_name : '',
                stream: ''
            }));
            if (value) fetchSubjects(value);
            else {
                setSubjects([]);
                setChapters([]);
            }
            return;
        }

        if (name === 'subject_id') {
            setFormData(prev => ({
                ...prev,
                subject_id: value,
                chapter_id: ''
            }));
            if (value) fetchChapters(value);
            else setChapters([]);
            return;
        }

        if (name === 'profile_language') {
            setFormData(prev => ({ ...prev, profile_language: value }));
            i18n.changeLanguage(value);
            localStorage.setItem('i18nextLng', value);
            return;
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setMessage({ type: 'info', text: t('auth:register.creatingAccount', 'Creating account...') });

        if (formData.password !== formData.confirmPassword) {
            setMessage({ type: 'error', text: t('auth:register.passwordsNoMatch', 'Passwords do not match.') });
            return;
        }


        try {
            const response = await fetch(`${API_BASE_URL}register.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(formData)
            });
            const result = await response.json();

            if (result.status === 'success') {
                setMessage({ type: 'success', text: result.message + " " + t('auth:register.startingTest', "Starting your test...") });

                // Resolve display names from local lists
                const boardName = boards.find(b => String(b.id) === String(formData.board_id))?.name || '';
                const className = classes.find(c => String(c.id) === String(formData.class_id))?.class_name || '';
                const subjectName = subjects.find(s => String(s.id) === String(formData.subject_id))?.subject_name || '';
                const chapterName = chapters.find(ch => String(ch.id) === String(formData.chapter_id))?.chapter_name || 'Chapter 1';

                // Save to localStorage for persistence across the app
                localStorage.setItem('userId', String(result.user_id));
                localStorage.setItem('studentClass', className);
                localStorage.setItem('studentName', formData.fullName);
                localStorage.setItem('boardId', formData.board_id);
                localStorage.setItem('classId', formData.class_id);
                // Also store full user object so Profile & other components read the correct userId
                localStorage.setItem('user', JSON.stringify({
                    id: result.user_id,
                    name: formData.fullName,
                    email: formData.email,
                    role: 'free',
                    class_name: className,
                    userId: result.user_id,
                    profile_language: formData.profile_language
                }));

                // Ensure the selected language is applied globally
                i18n.changeLanguage(formData.profile_language);
                localStorage.setItem('i18nextLng', formData.profile_language);


                setTimeout(() => {
                    navigate('/loader', {
                        state: {
                            board: boardName,
                            class: className,
                            subject: subjectName,
                            subject_id: formData.subject_id,
                            chapter: chapterName,
                            chapter_id: formData.chapter_id
                        }
                    });
                }, 1500);
            } else {
                setMessage({ type: 'error', text: result.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: t('auth:login.connectionError', 'Connection error. Please ensure PHP server is running.') });
        }

    };

    return (
        <div className="registration-page">
            <LandingHeader activePage="register" />


            <main className="container registration-main">
                <div className="registration-container">
                    <div className="registration-hero">
                        <div className="hero-content">
                            <h1 className="hero-title">{t('auth:register.title', 'Create Your AI Learning Account')}</h1>
                            <p className="hero-subtext">{t('auth:register.subtitle', 'Join thousands of students improving their skills daily with personalized AI guidance.')}</p>
                        </div>
                        <div className="hero-visual">
                            {/* The generated 3D hero image will be used here */}
                            <div className="floating-elements-fallback">
                                <span className="float-icon">🎓</span>
                                <span className="float-icon">📖</span>
                                <span className="float-icon">🧠</span>
                            </div>
                        </div>
                    </div>

                    <div className="registration-form-section">
                        {message.text && (
                            <div className={`auth-message ${message.type}`}>
                                {message.text}
                            </div>
                        )}
                        <form className="registration-form" onSubmit={handleSubmit}>
                             <div className="form-group">
                                <label>{t('auth:register.fullName', 'Full Name')}</label>
                                <input type="text" name="fullName" value={formData.fullName} placeholder={t('auth:register.fullNamePlaceholder', 'Enter your full name')} onChange={handleChange} required />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('auth:register.emailPhone', 'Email / Phone')}</label>
                                    <input type="text" name="email" value={formData.email} placeholder={t('auth:register.emailPhonePlaceholder', 'Enter your email or phone number')} onChange={handleChange} required autoComplete="off" />
                                </div>
                                <div className="form-group">
                                    <label>{t('common:language', 'Language')}</label>
                                    <select name="profile_language" value={formData.profile_language} onChange={handleChange} required>
                                        {availableLanguages.map(lang => (
                                            <option key={lang.id} value={lang.code}>
                                                {lang.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('auth:register.password', 'Password')}</label>
                                    <input type="password" name="password" value={formData.password} placeholder={t('auth:register.passwordPlaceholder', 'Create password')} onChange={handleChange} required autoComplete="new-password" />
                                </div>
                                <div className="form-group">
                                    <label>{t('auth:register.confirmPassword', 'Confirm Password')}</label>
                                    <input type="password" name="confirmPassword" value={formData.confirmPassword} placeholder={t('auth:register.confirmPasswordPlaceholder', 'Confirm password')} onChange={handleChange} required autoComplete="new-password" />
                                </div>
                            </div>


                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('auth:register.educationBoard', 'Education Board')}</label>
                                    <select name="board_id" value={formData.board_id} onChange={handleChange} required>
                                        <option value="">{t('auth:register.selectBoard', 'Select Board')}</option>
                                        {boards.map(board => <option key={board.id} value={board.id}>{board.name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>{t('auth:register.selectClass', 'Select Class')}</label>
                                    <select name="class_id" value={formData.class_id} onChange={handleChange} required disabled={!formData.board_id}>
                                        <option value="">{t('auth:register.selectClass', 'Select Class')}</option>
                                        {classes.map(cls => <option key={cls.id} value={cls.id}>{cls.class_name}</option>)}
                                    </select>
                                </div>
                            </div>


                            <div className="form-row">
                                <div className="form-group">
                                    <label>{t('auth:register.primarySubject', 'Primary Subject')}</label>
                                    <select name="subject_id" value={formData.subject_id} onChange={handleChange} required disabled={!formData.board_id}>
                                        <option value="">{t('auth:register.selectSubject', 'Select Subject')}</option>
                                        {subjects.map(subject => <option key={subject.id} value={subject.id}>{subject.subject_name}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>{t('auth:register.selectChapter', 'Select Chapter')}</label>
                                    <select name="chapter_id" value={formData.chapter_id} onChange={handleChange} required disabled={!formData.subject_id}>
                                        <option value="">{t('auth:register.selectChapter', 'Select Chapter')}</option>
                                        {chapters.map(chapter => (
                                            <option key={chapter.id} value={chapter.id}>
                                                {chapter.chapter_number ? `${chapter.chapter_number}. ` : ''}{chapter.chapter_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="form-row">
                                {(formData.studentClass.includes('11') || formData.studentClass.includes('12')) && (
                                    <div className="form-group">
                                        <label>{t('auth:register.stream', 'Stream')}</label>
                                        <select name="stream" value={formData.stream} onChange={handleChange} required>
                                            <option value="">{t('auth:register.selectStream', 'Select Stream')}</option>
                                            <option value="science">Science</option>
                                            <option value="commerce">Commerce</option>
                                            <option value="humanities">Humanities</option>
                                        </select>
                                    </div>
                                )}
                            </div>


                            <button type="submit" className="btn btn-primary full-width">{t('auth:register.submit', 'Sign Up')}</button>

                        </form>

                        {/* <div className="social-login">
                            <p>Or sign up with</p>
                            <div className="social-buttons">
                                <button className="social-btn google"><span className="social-icon">G</span> Google</button>
                                <button className="social-btn apple"><span className="social-icon">🍎</span> Apple</button>
                                <button className="social-btn facebook"><span className="social-icon">f</span> Facebook</button>
                            </div>
                        </div> */}

                        <footer>
                            <p>{t('auth:register.hasAccount', 'Already have an account?')} <Link to="/login">{t('auth:register.login', 'Login')}</Link></p>
                            {/* <div className="footer-socials">
                                <span className="glow-icon">🐦</span>
                                <span className="glow-icon">📸</span>
                                <span className="glow-icon">🔗</span>
                            </div> */}
                        </footer>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default RegistrationPage;
